/* eslint-disable no-console */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from "amazon-cognito-identity-js";

/**
 * Amplify を使わずに Cognito (ID/PW) + Federated SSO(Entra) を扱うラッパ
 * - 独自ログイン画面を維持（Hosted UI のUIは使わない）
 * - SSO は Cognito /oauth2/authorize へのリダイレクト（＝従来の Auth.federatedSignIn 相当）
 * - Vue2 / Vuex / Router 既存コード互換（idToken.jwtToken を維持）
 */
export default class Cognito {
  constructor() {
    this.userPool = null;
    this.options = null;
  }

  // 設定の更新（現行互換）
  configure(config) {
    // 既存コード互換：config.userPool が渡されたらそれを使う
    if (config && config.userPool) {
      this.userPool = config.userPool;
    } else {
      this.userPool = new CognitoUserPool({
        UserPoolId: config.UserPoolId,
        ClientId: config.ClientId
      });
    }

    this.options = config;
  }

  // Vue Plugin install（現行互換）
  static install = (Vue, options) => {
    Object.defineProperty(Vue.prototype, "$cognito", {
      get() {
        return this.$root._cognito;
      }
    });

    Vue.mixin({
      beforeCreate() {
        if (this.$options.cognito) {
          this._cognito = this.$options.cognito;
          this._cognito.configure(options);
        }
      }
    });
  };

  /* =========================================================
   * 1) Username/Password（独自ログイン画面）
   * ========================================================= */

  /**
   * username, password でサインアップ
   * username = email として UserAttribute にも登録
   */
  async signUp(username, password, familyName, givenName) {
    return new Promise((resolve, reject) => {
      this.userPool.signUp(
        username,
        password,
        [
          { Name: "email", Value: username },
          { Name: "given_name", Value: givenName },
          { Name: "family_name", Value: familyName }
        ],
        null,
        (err, result) => {
          if (err) return reject(err);
          resolve(result);
        }
      );
    });
  }

  /**
   * Cognitoログイン処理（ID/PW）
   * 戻り値は Vuex に格納される前提の「session互換オブジェクト」
   */
  async login(username, password) {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password
    });

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.userPool
    });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          this._storeCognitoSession(session);
          resolve(this._formatSessionLike(session));
        },
        onFailure: (err) => reject(err),
        // MFA / NEW_PASSWORD_REQUIRED 等を使っていないなら未実装でOK
        // 必要ならここを拡張する
      });
    });
  }

  /**
   * ログアウト（Cognito + local session clear）
   */
  async logout() {
    try {
      const user = this.userPool.getCurrentUser();
      if (user) user.signOut();
    } finally {
      // タブ単位保存を維持
      sessionStorage.removeItem("idToken");
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
    }
  }

  /* =========================================================
   * 2) SSO（Entra）: router/index.js から呼ばれる
   * ========================================================= */

  /**
   * SSOログイン処理（従来 Auth.federatedSignIn 相当）
   * router/index.js から呼ばれ、戻り値は使われない前提なので「即リダイレクト」する
   */
  federatedSignIn() {
    const domain = process.env.VUE_APP_COGNITO_USER_POOL_DOMAIN;
    const clientId = process.env.VUE_APP_COGNITO_CLIENT_ID;
    const redirectUri = process.env.VUE_APP_LECO_TOP_URL + "/sso-redirect/";
    const provider = process.env.VUE_APP_COGNITO_IDENTITY_PROVIDER;

    // Cognito Hosted UI “画面” は使わないが、OAuth エンドポイントは利用する（これがSSOの本体）
    const url =
      `https://${domain}/oauth2/authorize` +
      `?identity_provider=${encodeURIComponent(provider)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&client_id=${encodeURIComponent(clientId)}` +
      `&scope=${encodeURIComponent("openid")}`;

    window.location.href = url;
  }

  /**
   * /sso-redirect で code を受け取った後、token へ交換して sessionStorage に保存
   * 例：sso-redirect.vue で new URLSearchParams(location.search).get("code") を渡す
   */
  async completeSsoLogin(code) {
    if (!code) throw new Error("OAuth code is missing.");

    const domain = process.env.VUE_APP_COGNITO_USER_POOL_DOMAIN;
    const clientId = process.env.VUE_APP_COGNITO_CLIENT_ID;
    const redirectUri = process.env.VUE_APP_LECO_TOP_URL + "/sso-redirect/";

    const res = await fetch(`https://${domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: clientId,
        redirect_uri: redirectUri,
        code
      })
    });

    const token = await res.json();

    // Cognitoがエラーを返すことがある（invalid_grant等）
    if (!res.ok || token.error) {
      const msg =
        token.error_description ||
        token.error ||
        `token exchange failed: ${res.status}`;
      throw new Error(msg);
    }

    this._storeOauthToken(token);

    // Vuex が期待する形に寄せる
    return this._formatOauthTokenLike(token);
  }

  /* =========================================================
   * 3) 認証状態確認（router guard 用）
   * ========================================================= */

  /**
   * ログインしているか判定
   * 認証があれば「session互換オブジェクト」、なければ null
   */
  async checkAuthenticated() {
    const idToken = sessionStorage.getItem("idToken");
    if (!idToken) return null;

    // JWT exp チェック（従来の cognitoUser.idToken.payload.exp 相当）
    let payload;
    try {
      payload = this._decodeJwtPayload(idToken);
    } catch (e) {
      return null;
    }

    const exp = payload && payload.exp;
    const now = Math.floor(Date.now() / 1000);
    if (!exp || exp < now) return null;

    return {
      idToken: { jwtToken: idToken, payload },
      accessToken: { jwtToken: sessionStorage.getItem("accessToken") || "" },
      refreshToken: { token: sessionStorage.getItem("refreshToken") || "" }
    };
  }

  /* =========================================================
   * 4) パスワード系（Amplify Auth 代替）
   * ========================================================= */

  /**
   * パスワード初期化のための認証コード送信
   */
  async forgotPassword(userId) {
    const cognitoUser = new CognitoUser({
      Username: userId,
      Pool: this.userPool
    });

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: (data) => resolve(data),
        onFailure: (err) => reject(err),
        // inputVerificationCode はUI側で不要なら無視してOK
        inputVerificationCode: (data) => resolve(data)
      });
    });
  }

  /**
   * パスワード忘れ時の再設定
   */
  async forgotPasswordSubmit(userId, verificationCode, newPassword) {
    const cognitoUser = new CognitoUser({
      Username: userId,
      Pool: this.userPool
    });

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => resolve({ success: true }),
        onFailure: (err) => reject(err)
      });
    });
  }

  /**
   * パスワード変更
   * ※ CognitoUser が必要。現行コードで呼び出している user が Amplify の user だった場合は、
   *   呼び出し側を「currentUser から CognitoUser を作る」形に変える必要がある。
   *   ここでは互換のため、idToken から userName を推定して currentUser を使う実装にする。
   */
  async changePassword(_user, nowPassword, newPassword) {
    const currentUser = this.userPool.getCurrentUser();
    if (!currentUser) throw new Error("No current user.");

    // getSession でセッションが有効なことを確認してから changePassword
    return new Promise((resolve, reject) => {
      currentUser.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          return reject(err || new Error("Session is invalid."));
        }
        currentUser.changePassword(nowPassword, newPassword, (err2, result) => {
          if (err2) return reject(err2);
          resolve(result);
        });
      });
    });
  }

  /* =========================================================
   * 内部ユーティリティ
   * ========================================================= */

  _storeCognitoSession(session) {
    // タブ単位保存（現行踏襲）
    sessionStorage.setItem("idToken", session.getIdToken().getJwtToken());
    sessionStorage.setItem("accessToken", session.getAccessToken().getJwtToken());
    sessionStorage.setItem("refreshToken", session.getRefreshToken().getToken());
  }

  _storeOauthToken(token) {
    sessionStorage.setItem("idToken", token.id_token);
    sessionStorage.setItem("accessToken", token.access_token || "");
    if (token.refresh_token) {
      sessionStorage.setItem("refreshToken", token.refresh_token);
    } else {
      sessionStorage.removeItem("refreshToken");
    }
  }

  _formatSessionLike(session) {
    const idJwt = session.getIdToken().getJwtToken();
    const payload = this._decodeJwtPayload(idJwt);

    return {
      idToken: { jwtToken: idJwt, payload },
      accessToken: { jwtToken: session.getAccessToken().getJwtToken() },
      refreshToken: { token: session.getRefreshToken().getToken() }
    };
  }

  _formatOauthTokenLike(token) {
    const payload = this._decodeJwtPayload(token.id_token);
    return {
      idToken: { jwtToken: token.id_token, payload },
      accessToken: { jwtToken: token.access_token || "" },
      refreshToken: { token: token.refresh_token || "" }
    };
  }

  _decodeJwtPayload(jwt) {
    const parts = jwt.split(".");
    if (parts.length < 2) throw new Error("Invalid JWT");

    // base64url → base64
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");

    // padding 補完
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);

    // base64 decode → bytes
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    // UTF-8 decode → JSON
    const json = new TextDecoder("utf-8").decode(bytes);
    return JSON.parse(json);
  }
}
